import Splitter, { SplitDirection } from '@devbookhq/splitter'
import { css } from '@emotion/css'
import type { HTMLAttributes, Ref } from 'react'
import React, { useCallback } from 'react'

export interface VericalSplitterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'left' | 'right' | 'onResize'> {
  left: React.ReactNode
  /** Specify flex as the percentage the left pane should occupy. */
  defaultLeftPercentage?: number
  minWidths?: [number, number]
  right: React.ReactNode
  thickness?: number
  ref?: Ref<HTMLDivElement>
  /** onResize will return the leftFlex */
  onResize?(left: number): void
}

const ContentPane = (props: HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    style={{
      display: 'flex',
      flex: 1,
      height: '100%',
      marginLeft: 8,
      marginRight: 8,
      flexDirection: 'column',
      overflow: 'auto',
      ...props.style,
    }}
  />
)

export const VerticalSplitter = (props: VericalSplitterProps) => {
  const { left, right, ref, minWidths = [50, 50], defaultLeftPercentage = 50, onResize, style, ...restProps } = props

  const [leftFlex, setLeftFlex] = React.useState(defaultLeftPercentage ?? 50)

  const splitterGutterStyle = css({
    background: 'var(--tuna-palette-divider)',
    ' &:hover': {
      background: 'var(--tuna-palette-primary-main)',
    },
    width: '6.4px',
  })

  const SplitterDraggerStyle = css({
    backgroundColor: 'var(--tuna-palette-divider)',
  })

  const onResizePane = useCallback(
    (pairIdx: number, newSizes: number[]) => {
      if (pairIdx !== 0) {
        return
      }
      const flex = newSizes[0]
      setLeftFlex(flex)
      if (onResize) {
        onResize(flex)
      }
    },
    [onResize]
  )

  return (
    <div ref={ref} {...restProps} style={{ ...style, height: '100%' }}>
      <Splitter
        direction={SplitDirection.Horizontal}
        minWidths={minWidths}
        initialSizes={[leftFlex, 100 - leftFlex]}
        onResizeFinished={onResizePane}
        gutterClassName={splitterGutterStyle}
        draggerClassName={SplitterDraggerStyle}
      >
        <ContentPane>{left}</ContentPane>
        <ContentPane>{right}</ContentPane>
      </Splitter>
    </div>
  )
}

export default VerticalSplitter
